import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Trophy, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { API_BASE_URL, LOGO_BASE_URL } from '../../../apiConfig';

const LOGO_BASE = LOGO_BASE_URL + '/';
const PREMIUM_GRAD = 'linear-gradient(145deg, #0A1172 0%, #040960 100%)';
const FONT_RAJ = "'Rajdhani', sans-serif";
const FONT_BODY = "'Barlow', sans-serif";
const FONT_DISPLAY = "'Barlow Condensed', sans-serif";
const ACCENT_YELLOW = '#E7C424';

// ── Helpers ─────────────────────────────────────────────────────────────────

function getTopBatsman(sc, result) {
    if (!sc) return null;
    const firstClub = sc.first_batting_club_name || '';
    const secondClub = sc.second_batting_club_name || '';
    const first = (sc.first_batting_batting || []).map(b => ({ ...b, club: firstClub, against: secondClub }));
    const second = (sc.second_batting_batting || []).map(b => ({ ...b, club: secondClub, against: firstClub }));
    const all = [...first, ...second].filter(b => b.runs && b.runs !== '-' && !isNaN(parseInt(b.runs)));
    if (!all.length) return null;
    const top = all.reduce((m, b) => parseInt(b.runs) > parseInt(m.runs) ? b : m);
    const logoKey = top.club.toLowerCase().includes(result.left_club?.toLowerCase()?.split(' ')[0] || '___')
        ? 'left_team_logo' : 'right_team_logo';
    return { ...top, teamLogo: result[logoKey] || result.left_team_logo };
}

function getTopBowler(sc, result) {
    if (!sc) return null;
    const firstClub = sc.first_batting_club_name || '';
    const secondClub = sc.second_batting_club_name || '';
    const first = (sc.first_batting_bowling || []).map(b => ({ ...b, club: secondClub, against: firstClub }));
    const second = (sc.second_batting_bowling || []).map(b => ({ ...b, club: firstClub, against: secondClub }));
    const all = [...first, ...second].filter(b => b.wickets && !isNaN(parseInt(b.wickets)) && parseInt(b.wickets) > 0);
    if (!all.length) return null;
    const top = all.reduce((m, b) => parseInt(b.wickets) > parseInt(m.wickets) ? b : m);
    const logoKey = top.club.toLowerCase().includes(result.right_club?.toLowerCase()?.split(' ')[0] || '___')
        ? 'right_team_logo' : 'left_team_logo';
    return { ...top, teamLogo: result[logoKey] || result.right_team_logo };
}

function batAchievement(runs) {
    const r = parseInt(runs);
    if (r >= 100) return 'Century';
    if (r >= 50) return 'Half-Century';
    return null;
}
function bowlAchievement(wkts) {
    const w = parseInt(wkts);
    if (w >= 5) return 'Five-For';
    if (w >= 3) return 'Hat-Trick';
    return null;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Dots({ total, current, onDotClick }) {
    const count = Math.min(total, 8);
    return (
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '8px', zIndex: 5 }}>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} onClick={() => onDotClick && onDotClick(i)}
                    style={{
                        width: i === current ? '24px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        background: i === current ? ACCENT_YELLOW : 'rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                    }}
                />
            ))}
        </div>
    );
}

function TeamLogo({ logo, size = 48 }) {
    const [error, setError] = useState(false);
    return (
        <div style={{
            width: `${size}px`, height: `${size}px`, 
            background: 'rgba(255,255,255,0.05)', 
            borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
            border: '1px solid rgba(255,255,255,0.1)'
        }}>
            {(logo && !error)
                ? <img 
                    src={LOGO_BASE + logo} 
                    alt="" 
                    style={{ width: '80%', height: '80%', objectFit: 'contain' }} 
                    onError={() => setError(true)} 
                />
                : <Trophy size={size * 0.5} color="rgba(255,255,255,0.2)" />
            }
        </div>
    );
}

function AchievementBadge({ label }) {
    if (!label) return null;
    return (
        <div style={{ 
            padding: '4px 10px', 
            background: 'rgba(231,196,36,0.15)', 
            borderRadius: '4px', 
            border: `1px solid ${ACCENT_YELLOW}`,
            flexShrink: 0 
        }}>
            <span style={{ 
                fontFamily: FONT_DISPLAY, fontWeight: 700, 
                fontSize: '11px', color: ACCENT_YELLOW, 
                textTransform: 'uppercase', letterSpacing: '0.5px' 
            }}>{label}</span>
        </div>
    );
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function RecentResults() {
    const [results, setResults] = useState([]);
    const [scorecards, setScorecards] = useState({});
    const [topIdx, setTopIdx] = useState(0);
    const [batIdx, setBatIdx] = useState(0);
    const [bowlIdx, setBowlIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const { isMobile, isTablet } = useBreakpoint();

    useEffect(() => {
        axios.get(`${API_BASE_URL}/divisionResult/137680`)
            .then(res => {
                if (res.data && res.data.length > 0) {
                    setResults(res.data);
                    res.data.slice(0, 8).forEach(r => {
                        axios.get(`${API_BASE_URL}/scoreCardDetails/${r.id}`)
                            .then(sc => setScorecards(prev => ({ ...prev, [r.id]: sc.data })))
                            .catch(() => {});
                    });
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return null;
    
    const isEmpty = results.length === 0;

    const batPerformers = results
        .filter(r => scorecards[r.id])
        .map(r => { const b = getTopBatsman(scorecards[r.id], r); return b ? { ...b, result: r } : null; })
        .filter(Boolean);

    const bowlPerformers = results
        .filter(r => scorecards[r.id])
        .map(r => { const b = getTopBowler(scorecards[r.id], r); return b ? { ...b, result: r } : null; })
        .filter(Boolean);

    const topResult = results[topIdx] || results[0];
    const currentBat = batPerformers.length > 0 ? batPerformers[batIdx % batPerformers.length] : null;
    const currentBowl = bowlPerformers.length > 0 ? bowlPerformers[bowlIdx % bowlPerformers.length] : null;

    const SectionHeader = ({ title }) => (
        <h2 style={{
            fontFamily: 'var(--fontDisplay)',
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 800,
            color: '#000',
            margin: '0 0 24px 0',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        }}>
            {title}
        </h2>
    );

    return (
        <div style={{
            maxWidth: 'var(--maxWidth)', margin: '0 auto 60px auto',
            padding: isMobile ? '0 16px' : '0 24px',
            width: '100%', boxSizing: 'border-box'
        }}>
            <SectionHeader title="Recent Results" />
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!isEmpty ? (
                    <>
                        {/* ── TOP SECTION (Featured Match) ── */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                padding: isMobile ? '24px 16px' : '20px 40px',
                                background: '#040960',
                                borderRadius: '0px',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: isMobile ? '32px' : '40px',
                                border: '1px solid rgba(255,255,255,0.08)',
                            }}
                        >
                            <div style={{ flex: 1, position: 'relative', zIndex: 1, minWidth: 0 }}>
                                <div style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', 
                                    color: 'rgba(255,255,255,0.7)', fontFamily: FONT_DISPLAY, 
                                    fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px',
                                    marginBottom: '8px'
                                }}>
                                    <span>{topResult?.match_date || 'TBD'}</span>
                                    <span style={{ width: '4px', height: '4px', background: '#fff', borderRadius: '50%' }} />
                                    <span>Premier</span>
                                </div>
                                <h3 style={{ 
                                    fontFamily: FONT_DISPLAY, fontWeight: 700, 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    lineHeight: '1.2', color: '#fff', margin: '0 0 15px 0'
                                }}>
                                    {topResult?.msg || 'Match Summary'}
                                </h3>
                                <Link
                                    to={`/view-score-card/${topResult?.id}`}
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 16px', background: '#fff', color: '#040960',
                                        textDecoration: 'none', fontFamily: FONT_DISPLAY, 
                                        fontWeight: 800, fontSize: '12px', textTransform: 'uppercase',
                                        transition: 'all 0.2s ease', border: 'none'
                                    }}>
                                    Score Card <ChevronRight size={14} />
                                </Link>
                            </div>

                            <div style={{ 
                                flex: 1.5, display: 'flex', flexDirection: 'column', 
                                alignItems: 'center', gap: '15px', position: 'relative', zIndex: 1 
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '15px' : '30px', width: '100%', justifyContent: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flex: 1 }}>
                                        <div style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            <img src={LOGO_BASE + topResult?.left_team_logo} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                                        </div>
                                        <span style={{ fontFamily: FONT_RAJ, fontWeight: 700, fontSize: isMobile ? '18px' : '22px', color: '#fff' }}>{topResult?.left_run}/{topResult?.left_w}</span>
                                        <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '11px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', textTransform: 'uppercase' }}>{topResult?.left_club}</span>
                                    </div>

                                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: isMobile ? '28px' : '48px', fontWeight: 900, color: '#fff', fontStyle: 'italic' }}>V/S</div>

                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flex: 1 }}>
                                        <div style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            <img src={LOGO_BASE + topResult?.right_team_logo} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                                        </div>
                                        <span style={{ fontFamily: FONT_RAJ, fontWeight: 700, fontSize: isMobile ? '18px' : '22px', color: '#fff' }}>{topResult?.right_run}/{topResult?.right_w}</span>
                                        <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '11px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', textTransform: 'uppercase' }}>{topResult?.right_club}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    {results.slice(0, 8).map((_, i) => (
                                        <div key={i} onClick={() => setTopIdx(i)}
                                            style={{
                                                width: '6px', height: '6px', borderRadius: '50%',
                                                background: i === topIdx ? '#fff' : 'rgba(255,255,255,0.3)',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* ── BOTTOM SECTION (Performers) ── */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: isMobile ? '16px' : '24px',
                            width: '100%',
                        }}>
                            <PerformanceCard
                                type="Batsman"
                                performer={currentBat}
                                dotsTotal={batPerformers.length}
                                dotsCurrent={batIdx}
                                onDotClick={setBatIdx}
                                isMobile={isMobile}
                            />
                            <PerformanceCard
                                type="Bowler"
                                performer={currentBowl}
                                dotsTotal={bowlPerformers.length}
                                dotsCurrent={bowlIdx}
                                onDotClick={setBowlIdx}
                                isMobile={isMobile}
                            />
                        </div>
                    </>
                ) : (
                    <MatchEmptyState type="results" isMobile={isMobile} />
                )}
            </div>
        </div>
    );
}

function PerformanceCard({ type, performer, dotsTotal, dotsCurrent, onDotClick, isMobile }) {
    if (!performer) return null;

    const isBat = type === 'Batsman';
    const statName = isBat ? 'Runs' : 'Wickets';
    const achievement = isBat ? batAchievement(performer.runs) : bowlAchievement(performer.wickets);

    return (
        <motion.div 
            whileHover={{ y: -4, backgroundColor: 'rgba(255,255,255,0.02)' }}
            style={{
                background: PREMIUM_GRAD,
                padding: '20px',
                border: '1px solid rgba(255,255,255,0.08)',
                minHeight: isMobile ? 'auto' : '200px', // Same height as Top section on desktop
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                position: 'relative', zIndex: 1 
            }}>
                <div>
                   <span style={{ 
                       fontFamily: FONT_BODY, fontSize: '11px', color: 'rgba(255,255,255,0.4)', 
                       textTransform: 'uppercase', letterSpacing: '1px' 
                    }}>
                        {performer.result?.math_date}
                    </span>
                    <h4 style={{ 
                        fontFamily: FONT_RAJ, fontSize: '20px', fontWeight: 700, 
                        color: '#fff', margin: '4px 0 12px 0', lineHeight: 1.1 
                    }}>
                        {isBat ? performer.batsman_name : performer.bowler_name}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ 
                            fontFamily: FONT_DISPLAY, fontSize: '48px', fontWeight: 800, 
                            color: ACCENT_YELLOW, lineHeight: 1 
                        }}>
                            {isBat ? performer.runs : performer.wickets}
                        </span>
                        <span style={{ fontFamily: FONT_BODY, fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>
                            {statName}
                        </span>
                    </div>
                    <AchievementBadge label={achievement} />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <TeamLogo logo={performer.teamLogo} size={36} />
                    <span style={{ 
                        fontFamily: FONT_DISPLAY, fontSize: '12px', color: 'rgba(255,255,255,0.6)',
                        textTransform: 'uppercase', textAlign: 'right', maxWidth: '100px'
                    }}>
                        {performer.club}
                    </span>
                </div>
            </div>

            <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                position: 'relative', zIndex: 1 
            }}>
                <div style={{ 
                    fontFamily: FONT_BODY, fontSize: '12px', color: 'rgba(255,255,255,0.5)',
                    display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                    Against <span style={{ color: '#fff', fontWeight: 600 }}>{performer.against}</span>
                </div>
                <Dots total={dotsTotal} current={dotsCurrent} onDotClick={onDotClick} />
            </div>

            {/* Subtle Performer Backdrop */}
            <div style={{ 
                position: 'absolute', right: '-10px', bottom: '0px', 
                opacity: 0.05, pointerEvents: 'none' 
            }}>
                 <svg viewBox="0 0 80 130" width="120" height="150" fill="#fff">
                    <circle cx="40" cy="16" r="14" />
                    <path d="M22 38 Q40 28 58 38 L62 88 L48 88 L40 58 L32 88 L18 88 Z" />
                    <path d="M22 40 L8 78 L18 80 L28 52" />
                    <path d="M58 40 L72 74 L62 76 L52 50" />
                    <path d="M18 88 L14 128 L26 128 L30 100 L34 128 L46 128 L50 100 L54 128 L66 128 L62 88 Z" />
                </svg>
            </div>
        </motion.div>
    );
}

function MatchEmptyState({ type, isMobile }) {
    const isFixtures = type === 'fixtures';
    return (
        <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '40px 20px' : '80px 40px',
            background: 'rgba(6, 13, 138, 0.02)',
            border: '2px dashed rgba(6, 13, 138, 0.1)',
            borderRadius: '4px',
            textAlign: 'center',
            boxSizing: 'border-box'
        }}>
            <div style={{ opacity: 0.1, marginBottom: '16px' }}>
                <Trophy size={64} color="#060D8A" />
            </div>
            <h3 style={{ 
                fontFamily: 'var(--fontDisplay)', 
                fontWeight: 900, 
                fontSize: isMobile ? '20px' : '24px', 
                color: '#060D8A', 
                margin: '0 0 8px 0', 
                textTransform: 'uppercase',
                fontStyle: 'italic'
            }}>
                Results Pending
            </h3>
            <p style={{ 
                fontFamily: 'var(--fontDisplay)', 
                fontSize: isMobile ? '13px' : '15px', 
                color: 'rgba(0,0,0,0.5)', 
                margin: 0,
                fontWeight: 600,
                textTransform: 'uppercase'
            }}>
                No match results are currently available for this season
            </p>
        </div>
    );
}
